import React from "react";
import { Download, Inbox } from "lucide-react";
import { API } from "@/lib/api";
import { t, HULL_LABELS, STATUS_LABEL } from "@/lib/i18n";

const fmtUSD = (n) => `$${(n || 0).toLocaleString("en-US")}`;
const fmtDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === "vi" ? "vi-VN" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function ReferralsTable({ rows, lang }) {
  const onExport = async () => {
    const token = localStorage.getItem("eigoo_token");
    const res = await fetch(`${API}/referrals/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `eigoo-referrals-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section
      className="eigoo-card p-6 sm:p-7 anim-fade-up anim-delay-4"
      data-testid="referrals-section"
    >
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h3 className="font-display text-lg sm:text-xl font-bold">
            {t(lang, "my_referrals")}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">{t(lang, "my_referrals_sub")}</p>
        </div>
        <button
          onClick={onExport}
          className="ghost-btn inline-flex items-center gap-2 text-sm"
          data-testid="export-csv-button"
        >
          <Download className="w-4 h-4" />
          {t(lang, "export_csv")}
        </button>
      </div>

      {rows.length === 0 ? (
        <div
          className="border border-dashed border-[#1e3a61] rounded-xl py-12 text-center bg-[#0f2038]/40"
          data-testid="referrals-empty"
        >
          <div className="w-12 h-12 mx-auto rounded-full grid place-items-center bg-[#132744] border border-[#1e3a61]">
            <Inbox className="w-5 h-5 text-slate-400" />
          </div>
          <p className="mt-4 font-semibold text-slate-200">{t(lang, "empty_referrals")}</p>
          <p className="text-xs text-slate-500 mt-1">{t(lang, "empty_referrals_sub")}</p>
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="referrals-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-[#1e3a61]">
                <th className="py-3 pr-4 font-semibold">{t(lang, "col_created")}</th>
                <th className="py-3 pr-4 font-semibold">{t(lang, "col_prospect")}</th>
                <th className="py-3 pr-4 font-semibold hidden sm:table-cell">
                  {t(lang, "col_business")}
                </th>
                <th className="py-3 pr-4 font-semibold">{t(lang, "col_hull")}</th>
                <th className="py-3 pr-4 font-semibold">{t(lang, "col_status")}</th>
                <th className="py-3 pr-4 font-semibold text-right">
                  {t(lang, "col_commission")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-[#1e3a61]/50 hover:bg-[#1a3254]/40 transition-colors"
                  data-testid={`referral-row-${r.id}`}
                >
                  <td className="py-3 pr-4 text-slate-300 whitespace-nowrap">
                    {fmtDate(r.created_at, lang)}
                  </td>
                  <td className="py-3 pr-4">
                    <div className="font-semibold text-white">{r.prospect_name}</div>
                    {r.email && (
                      <div className="text-[11px] text-slate-500">{r.email}</div>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-slate-300 hidden sm:table-cell">
                    {r.business_name || "—"}
                  </td>
                  <td className="py-3 pr-4 text-slate-300">
                    {HULL_LABELS[lang]?.[r.hull_id] || r.hull_name}
                  </td>
                  <td className="py-3 pr-4">
                    <span className={`status-pill status-${r.status}`}>
                      {STATUS_LABEL(lang, r.status)}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-right font-mono tabular-nums font-semibold text-[#f3c669]">
                    {r.status === "signed" ? fmtUSD(r.commission_usd || 50) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
